import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RequestStatus } from '../../common/types/request-status'

import { fetchTasks } from './exam-actions'

import { Task } from 'common/types/entities'

export type ResultType = {
  answers: string[]
  status: boolean | null
  taskId: string
}

export const examSlice = createSlice({
  name: 'exam',
  initialState: {
    tasks: [] as Task[],
    results: [] as ResultType[],
    currentTaskNumber: 0,
    isFinished: false,
    status: 'idle' as RequestStatus,
    error: null as null | string,
  },
  reducers: {
    setTasks(state, action: PayloadAction<Task[]>) {
      state.tasks = action.payload
      state.results = action.payload.map(task => ({
        answers: [],
        status: null,
        taskId: task.id,
      }))
      state.currentTaskNumber = 0
    },
    setCurrentTaskNumber(state, action: PayloadAction<number>) {
      state.currentTaskNumber = action.payload
    },
    goToNextTask(state) {
      if (state.currentTaskNumber === state.tasks.length - 1) {
        state.currentTaskNumber = 0
      } else {
        state.currentTaskNumber = state.currentTaskNumber + 1
      }
    },
    goToPrevTask(state) {
      if (state.currentTaskNumber === 0) {
        state.currentTaskNumber = state.tasks.length - 1
      } else {
        state.currentTaskNumber = state.currentTaskNumber - 1
      }
    },
    setTaskResult(state, action: PayloadAction<string[]>) {
      const task = state.tasks[state.currentTaskNumber]
      const isSuccess = task.answers.every(answer => {
        return answer.isCorrect
          ? action.payload.includes(answer.id)
          : !action.payload.includes(answer.id)
      })

      state.results[state.currentTaskNumber] = {
        answers: action.payload,
        status: isSuccess,
        taskId: task.id,
      }
    },
    finishExam(state) {
      state.isFinished = true
    },
    resetExam(state) {
      state.results = []
      state.tasks = []
      state.currentTaskNumber = 0
      state.isFinished = false
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchTasks.fulfilled, (state, action) => {
      state.tasks = action.payload!
      state.results = action.payload!.map(task => ({
        answers: [],
        status: null,
        taskId: task.id,
      }))
      state.currentTaskNumber = 0
    })
    builder.addMatcher(
      action => action.type.endsWith('/pending'),
      state => {
        state.status = 'loading'
        state.error = null
      }
    )
    builder.addMatcher(
      action => action.type.endsWith('/fulfilled'),
      state => {
        state.status = 'succeeded'
        state.error = null
      }
    )
    builder.addMatcher(
      action => action.type.endsWith('/rejected'),
      (state, action) => {
        state.status = 'failed'
        state.error = action.payload || action.error.message
      }
    )
  },
})

export const {
  setTasks,
  goToNextTask,
  goToPrevTask,
  setCurrentTaskNumber,
  setTaskResult,
  resetExam,
  finishExam,
} = examSlice.actions
export const examReducer = examSlice.reducer
